/**
 * services/MFAService.ts
 * Multi-Factor Authentication service layer
 *
 * Provides type-safe interface to MFA Edge Functions.
 * All methods return MFAResult<T> for consistent error handling.
 *
 * Work Unit: WU3 - Task 4B - Admin MFA Backend Implementation
 * Phase: Phase 1 - Frontend Service Layer
 */

import { supabase as supabaseClient } from '@/lib/supabase';
import type {
  MFAResult,
  MFAError,
  MFAErrorCode,
  Factor,
  FactorStatus,
  EnrollStartResponse,
  ChallengeResponse,
  ReauthTouchResponse,
} from '@/types/mfa';
import { MFAErrorCode as ErrorCode } from '@/types/mfa';
import { mfaFactorsRepository } from '@/repositories/mfaFactorsRepository';

// ===== PRIVATE TYPES =====

/**
 * Edge Function invocation options
 */
interface InvokeOptions<TRequest, TResponse> {
  /** Edge Function name */
  functionName: string;

  /** Request body */
  body: TRequest;

  /** Response validator (lightweight runtime check) */
  validate: (data: unknown) => data is TResponse;
}

/**
 * Cached user ID with timestamp
 */
interface CachedUserId {
  id: string;
  timestamp: number;
}

// ===== DEFAULT ERROR MESSAGES =====

/**
 * Default friendly error messages for each error code
 *
 * UI can override these, but provides sensible defaults.
 */
const DEFAULT_MESSAGES: Record<MFAErrorCode, string> = {
  [ErrorCode.INVALID_REQUEST]: 'Invalid request. Please try again.',
  [ErrorCode.UNAUTHORIZED]: 'You must be logged in to perform this action.',
  [ErrorCode.NOT_REAUTHED]:
    'Please re-enter your password to confirm this action.',
  [ErrorCode.ALREADY_ENROLLED]: 'MFA is already enabled on your account.',
  [ErrorCode.NOT_ENROLLED]: 'MFA is not enabled on your account.',
  [ErrorCode.INVALID_CODE]: 'Invalid authentication code. Please try again.',
  [ErrorCode.CHALLENGE_EXPIRED]:
    'Authentication code expired. Please request a new code.',
  [ErrorCode.CHALLENGE_CONFLICT]:
    'A verification is already in progress. Please try again.',
  [ErrorCode.CLOCK_SKEW]:
    'Invalid code. Please check that your device time is accurate.',
  [ErrorCode.FACTOR_NOT_FOUND]: 'MFA factor not found or already removed.',
  [ErrorCode.DENIED_LAST_FACTOR]:
    'Cannot disable MFA. This is your only authentication factor.',
  [ErrorCode.RATE_LIMITED]: 'Too many attempts. Please try again shortly.',
  [ErrorCode.FEATURE_DISABLED]: 'MFA is currently unavailable.',
  [ErrorCode.SERVER_ERROR]:
    'An error occurred. Please try again or contact support.',
};

// ===== MFA SERVICE =====

/**
 * MFA Service
 *
 * Singleton service for all MFA operations.
 *
 * Features:
 * - Type-safe Edge Function calls with validation
 * - Automatic error mapping and friendly messages
 * - User ID caching (10s TTL)
 * - Factor list caching (10s TTL, invalidated on enroll/unenroll)
 * - Clock skew detection
 * - Default factor selection for login flow
 * - Client-side telemetry (no PII)
 */
class MFAService {
  // ===== PRIVATE STATE =====

  /** Supabase client instance */
  private supabase = supabaseClient;

  /** Cached user ID with 10-second TTL */
  private cachedUserId: CachedUserId | undefined;

  /** Cached factor list with 10-second TTL */
  private cachedFactors: { factors: Factor[]; timestamp: number } | undefined;

  /** Cache TTL in milliseconds (10 seconds) */
  private readonly CACHE_TTL = 10_000;

  // ===== PRIVATE HELPERS: USER ID =====

  /**
   * Get current user ID with caching
   *
   * Caches for 10 seconds to avoid repeated Supabase calls.
   * Cache is invalidated on auth state changes (handled by AuthProvider).
   *
   * @returns User ID
   * @throws Error if user is not authenticated
   */
  private async getUserId(): Promise<string> {
    const now = Date.now();

    // Check cache
    if (
      this.cachedUserId &&
      now - this.cachedUserId.timestamp < this.CACHE_TTL
    ) {
      return this.cachedUserId.id;
    }

    // Fetch from Supabase
    const {
      data: { user },
      error,
    } = await supabaseClient.auth.getUser();

    if (error || !user) {
      throw new Error('Not authenticated');
    }

    // Update cache
    this.cachedUserId = { id: user.id, timestamp: now };

    return user.id;
  }

  /**
   * Invalidate user ID cache
   *
   * Called by AuthProvider on auth state changes.
   */
  public invalidateUserCache(): void {
    this.cachedUserId = undefined;
  }

  // ===== PRIVATE HELPERS: ERROR HANDLING =====

  /**
   * Map Edge Function error_type to MFAErrorCode
   *
   * Provides type-safe error codes for programmatic handling.
   *
   * @param errorType - Edge Function error_type string
   * @returns MFAErrorCode enum value
   */
  private mapErrorType(errorType: string): MFAErrorCode {
    switch (errorType) {
      case 'invalid_request':
        return ErrorCode.INVALID_REQUEST;
      case 'unauthorized':
        return ErrorCode.UNAUTHORIZED;
      case 'not_reauthed':
        return ErrorCode.NOT_REAUTHED;
      case 'already_enrolled':
        return ErrorCode.ALREADY_ENROLLED;
      case 'not_enrolled':
        return ErrorCode.NOT_ENROLLED;
      case 'invalid_code':
        return ErrorCode.INVALID_CODE;
      case 'challenge_expired':
        return ErrorCode.CHALLENGE_EXPIRED;
      case 'challenge_conflict':
        return ErrorCode.CHALLENGE_CONFLICT;
      case 'denied_last_factor':
        return ErrorCode.DENIED_LAST_FACTOR;
      case 'rate_limited':
        return ErrorCode.RATE_LIMITED;
      case 'feature_disabled':
        return ErrorCode.FEATURE_DISABLED;
      default:
        return ErrorCode.SERVER_ERROR;
    }
  }

  /**
   * Parse Retry-After header to seconds
   *
   * @param retryAfter - Retry-After header value (string or null)
   * @returns Seconds to wait, or undefined if not present/invalid
   */
  private parseRetryAfter(retryAfter?: string | null): number | undefined {
    if (!retryAfter) return undefined;

    const seconds = Number(retryAfter);
    return Number.isFinite(seconds) && seconds > 0 ? seconds : undefined;
  }

  /**
   * Create MFAError from Edge Function response
   *
   * Handles special cases:
   * - Clock skew detection (invalid_code + server_time)
   * - Rate limiting (parse Retry-After header)
   *
   * @param errorData - Error data from Edge Function
   * @param retryAfter - Retry-After header from response
   * @returns MFAError object
   */
  private createError(
    errorData: { error_type?: string; message?: string; server_time?: string },
    retryAfter?: string | null
  ): MFAError {
    const errorType = errorData.error_type || 'server_error';
    let errorCode = this.mapErrorType(errorType);

    // Clock skew detection: invalid_code + server_time present
    if (errorType === 'invalid_code' && errorData.server_time) {
      errorCode = ErrorCode.CLOCK_SKEW;
    }

    // Build error object
    const error: MFAError = {
      error_code: errorCode,
      message: errorData.message || DEFAULT_MESSAGES[errorCode],
    };

    // Add retry_after for rate limiting
    if (errorCode === ErrorCode.RATE_LIMITED) {
      const retryAfterSeconds = this.parseRetryAfter(retryAfter);
      if (retryAfterSeconds !== undefined) {
        error.retry_after = retryAfterSeconds;
      }
    }

    // Add server_time for clock skew
    if (errorCode === ErrorCode.CLOCK_SKEW && errorData.server_time) {
      error.server_time = errorData.server_time;
    }

    return error;
  }

  // ===== PRIVATE HELPERS: EDGE FUNCTION INVOCATION =====

  /**
   * Invoke Edge Function with unified error handling and validation
   *
   * DRY helper used by all public methods.
   *
   * Features:
   * - Automatic error mapping
   * - Response validation
   * - Client-side telemetry
   * - PII redaction in logs
   *
   * @param options - Invocation options
   * @returns MFAResult with validated response or error
   */
  private async invoke<TRequest, TResponse>(
    options: InvokeOptions<TRequest, TResponse>
  ): Promise<MFAResult<TResponse>> {
    const startTime = performance.now();
    const { functionName, body, validate } = options;

    try {
      // Emit telemetry: call start
      this.emitTelemetry({
        event: 'mfa_service_call_start',
        function: functionName,
      });

      // Invoke Edge Function
      const { data, error } = await supabaseClient.functions.invoke(functionName, {
        body: body as Record<string, unknown>,
      });

      const duration = performance.now() - startTime;

      // Handle invocation errors (HTTP errors, network errors, etc.)
      if (error) {
        const mfaError = this.createError(
          {
            error_type: error.message?.includes('Failed to fetch')
              ? 'server_error'
              : 'server_error',
            message: error.message,
          },
          null
        );

        this.emitTelemetry({
          event: 'mfa_service_call_error',
          function: functionName,
          error_code: mfaError.error_code,
          duration,
        });

        return { success: false, error: mfaError };
      }

      // Handle Edge Function errors (error_type in response)
      if (data && 'error_type' in data) {
        const mfaError = this.createError(
          data as { error_type?: string; message?: string; server_time?: string },
          null // Retry-After would be in headers, not body
        );

        this.emitTelemetry({
          event: 'mfa_service_call_error',
          function: functionName,
          error_code: mfaError.error_code,
          duration,
        });

        return { success: false, error: mfaError };
      }

      // Validate response shape
      if (!validate(data)) {
        const mfaError: MFAError = {
          error_code: ErrorCode.SERVER_ERROR,
          message: 'Malformed response from server',
        };

        this.emitTelemetry({
          event: 'mfa_service_call_error',
          function: functionName,
          error_code: mfaError.error_code,
          duration,
        });

        return { success: false, error: mfaError };
      }

      // Success
      this.emitTelemetry({
        event: 'mfa_service_call_success',
        function: functionName,
        duration,
      });

      return { success: true, data };
    } catch (error) {
      const duration = performance.now() - startTime;

      const mfaError: MFAError = {
        error_code: ErrorCode.SERVER_ERROR,
        message:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
      };

      this.emitTelemetry({
        event: 'mfa_service_call_error',
        function: functionName,
        error_code: mfaError.error_code,
        duration,
      });

      return { success: false, error: mfaError };
    }
  }

  // ===== PRIVATE HELPERS: TELEMETRY =====

  /**
   * Emit client-side telemetry event
   *
   * Wrapped in try-catch to never break main flow.
   * Never logs PII (codes, secrets, exact IPs).
   *
   * @param payload - Telemetry data
   */
  private emitTelemetry(payload: Record<string, unknown>): void {
    try {
      console.log(
        `📊 [MFA_TELEMETRY]`,
        JSON.stringify({
          ...payload,
          timestamp: new Date().toISOString(),
        })
      );
    } catch (error) {
      // Silently fail - telemetry errors should never break functionality
      console.warn('⚠️ MFA telemetry emission failed:', error);
    }
  }

  // ===== PRIVATE HELPERS: FACTOR SELECTION =====

  /**
   * Get default verified TOTP factor
   *
   * Used when factorId is optional in methods like verifyLogin/createChallenge.
   * Selects the first verified TOTP factor.
   *
   * @returns Factor ID or error result
   */
  private async getDefaultFactor(): Promise<MFAResult<string>> {
    const factorsResult = await this.listFactors();

    if (!factorsResult.success) {
      return factorsResult;
    }

    const verifiedTotp = factorsResult.data.find(
      (f) => f.type === 'totp' && f.status === 'verified'
    );

    if (!verifiedTotp) {
      return {
        success: false,
        error: {
          error_code: ErrorCode.NOT_ENROLLED,
          message: 'No verified MFA factors found. Please enroll first.',
        },
      };
    }

    return { success: true, data: verifiedTotp.id };
  }

  // ===== PUBLIC METHODS =====

  /**
   * List enrolled MFA factors
   *
   * Calls Supabase SDK directly (not an Edge Function).
   * Caches results for 10 seconds, invalidated on enroll/unenroll.
   *
   * @returns List of enrolled factors
   */
  async listFactors(): Promise<MFAResult<Factor[]>> {
    const startTime = performance.now();

    try {
      // Check cache
      const now = Date.now();
      if (
        this.cachedFactors &&
        now - this.cachedFactors.timestamp < this.CACHE_TTL
      ) {
        this.emitTelemetry({
          event: 'mfa_list_factors_cache_hit',
          duration: performance.now() - startTime,
        });

        return { success: true, data: this.cachedFactors.factors };
      }

      // Fetch from Supabase
      const { data, error } = await supabaseClient.auth.mfa.listFactors();

      const duration = performance.now() - startTime;

      if (error) {
        this.emitTelemetry({
          event: 'mfa_list_factors_error',
          error: error.message,
          duration,
        });

        return {
          success: false,
          error: {
            error_code: ErrorCode.SERVER_ERROR,
            message: error.message || 'Failed to list MFA factors',
          },
        };
      }

      // Transform to our Factor type (without labels yet)
      const factors: Factor[] =
        data?.totp?.map((factor) => ({
          id: factor.id,
          type: 'totp' as const,
          status: (factor.factor_type === 'totp' ? 'verified' : 'unverified') as FactorStatus,
          ...(factor.friendly_name ? { friendly_name: factor.friendly_name } : {}),
          created_at: factor.created_at,
        })) || [];

      // Enrich with custom labels from mfa_factors table
      if (factors.length > 0) {
        const userId = await this.getUserId();
        const factorIds = factors.map(f => f.id);
        const mfaFactorData = await mfaFactorsRepository.getByFactorIds(factorIds);

        // SECURITY: Verify all factors belong to current user
        const invalidFactors = mfaFactorData.filter(f => f.userId !== userId);
        if (invalidFactors.length > 0) {
          console.warn('⚠️ MFAService: Cross-user factor data detected, skipping enrichment');
        } else {
          // Merge labels into factors
          factors.forEach(factor => {
            const mfaFactor = mfaFactorData.find(f => f.factorId === factor.id);
            if (mfaFactor) {
              // Only assign label if not null (Factor.label is optional string, not nullable)
              if (mfaFactor.label !== null) {
                factor.label = mfaFactor.label;
              }
              if (mfaFactor.lastUsedAt) {
                factor.last_used_at = mfaFactor.lastUsedAt;
              }
            }
          });
        }
      }

      // Update cache
      this.cachedFactors = { factors, timestamp: now };

      this.emitTelemetry({
        event: 'mfa_list_factors_success',
        count: factors.length,
        duration,
      });

      return { success: true, data: factors };
    } catch (error) {
      const duration = performance.now() - startTime;

      this.emitTelemetry({
        event: 'mfa_list_factors_error',
        error: error instanceof Error ? error.message : 'Unknown error',
        duration,
      });

      return {
        success: false,
        error: {
          error_code: ErrorCode.SERVER_ERROR,
          message:
            error instanceof Error
              ? error.message
              : 'Failed to list MFA factors',
        },
      };
    }
  }

  /**
   * Start MFA enrollment
   *
   * Returns QR code and secret for setting up authenticator app.
   *
   * @returns Enrollment data with QR code, secret, and challenge
   */
  async enrollStart(): Promise<MFAResult<EnrollStartResponse>> {
    try {
      const userId = await this.getUserId();

      return await this.invoke<{ user_id: string }, EnrollStartResponse>({
        functionName: 'enroll-mfa',
        body: { user_id: userId },
        validate: (data): data is EnrollStartResponse =>
          typeof data === 'object' &&
          data !== null &&
          'factor_id' in data &&
          'qr_code' in data &&
          'secret' in data &&
          'otp_uri' in data &&
          'challenge_id' in data &&
          'expires_at' in data,
      });
    } catch (error) {
      return {
        success: false,
        error: {
          error_code: ErrorCode.UNAUTHORIZED,
          message: error instanceof Error ? error.message : 'Not authenticated',
        },
      };
    }
  }

  /**
   * Verify enrollment code (CLIENT-SIDE)
   *
   * Completes MFA enrollment by verifying the first TOTP code.
   * Uses client-side challenge + verify to avoid IP mismatch issues.
   * Includes preflight rate limiting and post-verify reporting.
   *
   * Architecture:
   * 1. Preflight gate (server-side rate limiting)
   * 2. Challenge creation (client-side - browser IP)
   * 3. Verification (client-side - browser IP)
   * 4. Report result (server-side telemetry + attempt tracking)
   *
   * @param factorId - Factor ID from enrollStart
   * @param challengeId - Challenge ID from enrollStart (ignored - fresh challenge created)
   * @param code - 6-digit TOTP code from authenticator app
   * @returns Success or error
   */
  async enrollVerify(
    factorId: string,
    _challengeId: string, // Prefixed with _ to indicate intentionally unused
    code: string
  ): Promise<MFAResult<void>> {
    try {
      const userId = await this.getUserId();

      // 1) Input sanitization
      const sanitizedCode = code.replace(/\D/g, '').padStart(6, '0').slice(-6);
      if (sanitizedCode.length !== 6) {
        return {
          success: false,
          error: {
            error_code: ErrorCode.INVALID_CODE,
            message: 'Code must be 6 digits',
          },
        };
      }

      // 2) Preflight gate (rate limiting)
      this.emitTelemetry({
        event: 'mfa_preflight_check',
        function: 'enrollVerify',
        factor_id: factorId,
      });

      const preflightResult = await this.invoke<
        { user_id: string; op: string },
        { allowed: true; nonce?: string; retry_after?: number; attempt_count?: number }
      >({
        functionName: 'preflight-verify',
        body: {
          user_id: userId,
          op: 'totp',
        },
        validate: (data): data is { allowed: true; nonce?: string; retry_after?: number; attempt_count?: number } =>
          typeof data === 'object' && data !== null && 'allowed' in data,
      });

      if (!preflightResult.success) {
        return preflightResult; // Server error
      }

      if (preflightResult.data.allowed !== true) {
        // Rate limited
        return {
          success: false,
          error: {
            error_code: ErrorCode.RATE_LIMITED,
            message: 'Too many attempts. Please try again later.',
            retry_after: preflightResult.data.retry_after || 60,
          },
        };
      }

      const nonce = preflightResult.data.nonce;

      // 3) Create challenge CLIENT-SIDE (browser IP)
      this.emitTelemetry({
        event: 'mfa_challenge_create_clientside',
        function: 'enrollVerify',
        factor_id: factorId,
      });

      const { data: challengeData, error: challengeError } =
        await this.supabase.auth.mfa.challenge({
          factorId,
        });

      if (challengeError || !challengeData) {
        console.error('❌ Failed to create client-side challenge:', challengeError);

        // Report failure
        await this.reportVerify(userId, factorId, 'fail', 'challenge_missing', nonce);

        return {
          success: false,
          error: {
            error_code: ErrorCode.SERVER_ERROR,
            message: 'Failed to create verification challenge',
          },
        };
      }

      const freshChallengeId = challengeData.id;
      console.log('✅ Created fresh client-side challenge:', freshChallengeId);

      // 4) Verify CLIENT-SIDE (browser IP)
      this.emitTelemetry({
        event: 'mfa_verify_clientside',
        function: 'enrollVerify',
        factor_id: factorId,
        challenge_id: freshChallengeId,
      });

      const { data: verifyData, error: verifyError } =
        await this.supabase.auth.mfa.verify({
          factorId,
          challengeId: freshChallengeId,
          code: sanitizedCode,
        });

      // 5) Report result to server (telemetry + tracking)
      const errorType = verifyError
        ? this.classifyVerifyError(verifyError)
        : undefined;

      const reportResult = await this.reportVerify(
        userId,
        factorId,
        verifyError ? 'fail' : 'success',
        errorType,
        nonce
      );

      // 6) Handle result
      if (verifyError || !verifyData) {
        console.error('❌ Client-side verification failed:', verifyError);

        return {
          success: false,
          error: {
            error_code: ErrorCode.INVALID_CODE,
            message: this.mapVerifyErrorMessage(verifyError, reportResult),
          },
        };
      }

      // Success!
      console.log('✅ MFA enrollment verified successfully');
      this.cachedFactors = undefined; // Invalidate cache

      return {
        success: true,
        data: undefined,
      };
    } catch (error) {
      console.error('❌ enrollVerify error:', error);
      return {
        success: false,
        error: {
          error_code: ErrorCode.UNAUTHORIZED,
          message: error instanceof Error ? error.message : 'Not authenticated',
        },
      };
    }
  }

  /**
   * Verify login code
   *
   * Verifies TOTP code during login flow using CLIENT-SIDE verification.
   * Same pattern as enrollVerify() to avoid IP mismatch issues.
   *
   * Architecture:
   * 1. Preflight gate (server-side rate limiting)
   * 2. Challenge creation (client-side - browser IP)
   * 3. Verification (client-side - browser IP)
   * 4. Report result (server-side telemetry + attempt tracking)
   *
   * @param challengeId - Challenge ID from createChallenge (ignored - fresh challenge created)
   * @param code - 6-digit TOTP code from authenticator app
   * @param factorId - Optional factor ID (defaults to first verified TOTP)
   * @returns Success or error
   */
  async verifyLogin(
    _challengeId: string, // Prefixed with _ to indicate intentionally unused
    code: string,
    factorId?: string
  ): Promise<MFAResult<{ session?: { access_token: string; refresh_token: string; expires_at?: number } }>> {
    try {
      const userId = await this.getUserId();

      // Get factor ID if not provided
      let resolvedFactorId = factorId;
      if (!resolvedFactorId) {
        const defaultFactorResult = await this.getDefaultFactor();
        if (!defaultFactorResult.success) {
          return defaultFactorResult;
        }
        resolvedFactorId = defaultFactorResult.data;
      }

      // 1) Input sanitization
      const sanitizedCode = code.replace(/\D/g, '').padStart(6, '0').slice(-6);
      if (sanitizedCode.length !== 6) {
        return {
          success: false,
          error: {
            error_code: ErrorCode.INVALID_CODE,
            message: 'Code must be 6 digits',
          },
        };
      }

      // 2) Preflight gate (rate limiting)
      this.emitTelemetry({
        event: 'mfa_preflight_check',
        function: 'verifyLogin',
        factor_id: resolvedFactorId,
      });

      const preflightResult = await this.invoke<
        { user_id: string; op: string },
        { allowed: true; nonce?: string; retry_after?: number; attempt_count?: number }
      >({
        functionName: 'preflight-verify',
        body: {
          user_id: userId,
          op: 'totp',
        },
        validate: (data): data is { allowed: true; nonce?: string; retry_after?: number; attempt_count?: number } =>
          typeof data === 'object' && data !== null && 'allowed' in data,
      });

      if (!preflightResult.success) {
        return preflightResult; // Server error
      }

      if (preflightResult.data.allowed !== true) {
        // Rate limited
        return {
          success: false,
          error: {
            error_code: ErrorCode.RATE_LIMITED,
            message: 'Too many attempts. Please try again later.',
            retry_after: preflightResult.data.retry_after || 60,
          },
        };
      }

      const nonce = preflightResult.data.nonce;

      // 3) Create challenge CLIENT-SIDE (browser IP)
      this.emitTelemetry({
        event: 'mfa_challenge_create_clientside',
        function: 'verifyLogin',
        factor_id: resolvedFactorId,
      });

      const { data: challengeData, error: challengeError } =
        await this.supabase.auth.mfa.challenge({
          factorId: resolvedFactorId,
        });

      if (challengeError || !challengeData) {
        console.error('❌ Failed to create client-side challenge:', challengeError);

        // Report failure
        await this.reportVerify(userId, resolvedFactorId, 'fail', 'challenge_missing', nonce);

        return {
          success: false,
          error: {
            error_code: ErrorCode.SERVER_ERROR,
            message: 'Failed to create verification challenge',
          },
        };
      }

      const freshChallengeId = challengeData.id;
      console.log('✅ Created fresh client-side challenge for login:', freshChallengeId);

      // 4) Verify CLIENT-SIDE (browser IP)
      this.emitTelemetry({
        event: 'mfa_verify_clientside',
        function: 'verifyLogin',
        factor_id: resolvedFactorId,
        challenge_id: freshChallengeId,
      });

      const { data: verifyData, error: verifyError } =
        await this.supabase.auth.mfa.verify({
          factorId: resolvedFactorId,
          challengeId: freshChallengeId,
          code: sanitizedCode,
        });

      // 5) Report result to server (telemetry + tracking)
      const errorType = verifyError
        ? this.classifyVerifyError(verifyError)
        : undefined;

      const reportResult = await this.reportVerify(
        userId,
        resolvedFactorId,
        verifyError ? 'fail' : 'success',
        errorType,
        nonce
      );

      // 6) Handle result
      if (verifyError || !verifyData) {
        console.error('❌ Client-side login verification failed:', verifyError);

        return {
          success: false,
          error: {
            error_code: ErrorCode.INVALID_CODE,
            message: this.mapVerifyErrorMessage(verifyError, reportResult),
          },
        };
      }

      // Success! Return the upgraded AAL2 session
      console.log('✅ MFA login verified successfully');
      this.cachedFactors = undefined; // Invalidate cache

      return {
        success: true,
        data: { session: verifyData }, // Wrap in expected structure
      };
    } catch (error) {
      console.error('❌ verifyLogin error:', error);
      return {
        success: false,
        error: {
          error_code: ErrorCode.UNAUTHORIZED,
          message: error instanceof Error ? error.message : 'Not authenticated',
        },
      };
    }
  }

  /**
   * Create MFA challenge
   *
   * Creates a challenge for TOTP verification (required before verify).
   * If factorId is omitted, uses first verified TOTP factor.
   *
   * @param factorId - Optional factor ID (defaults to first verified TOTP)
   * @returns Challenge ID and expiration
   */
  async createChallenge(
    factorId?: string
  ): Promise<MFAResult<ChallengeResponse>> {
    try {
      const userId = await this.getUserId();

      // Get factor ID if not provided
      let resolvedFactorId = factorId;
      if (!resolvedFactorId) {
        const defaultFactorResult = await this.getDefaultFactor();
        if (!defaultFactorResult.success) {
          return defaultFactorResult;
        }
        resolvedFactorId = defaultFactorResult.data;
      }

      return await this.invoke<
        { user_id: string; factor_id: string },
        ChallengeResponse
      >({
        functionName: 'create-mfa-challenge',
        body: {
          user_id: userId,
          factor_id: resolvedFactorId,
        },
        validate: (data): data is ChallengeResponse =>
          typeof data === 'object' &&
          data !== null &&
          'challenge_id' in data &&
          'expires_at' in data &&
          'created_at' in data &&
          'factor_id' in data,
      });
    } catch (error) {
      return {
        success: false,
        error: {
          error_code: ErrorCode.UNAUTHORIZED,
          message: error instanceof Error ? error.message : 'Not authenticated',
        },
      };
    }
  }

  /**
   * Unenroll MFA factor
   *
   * Removes MFA factor from account.
   * On success, invalidates factor cache.
   *
   * @param factorId - Factor ID to remove
   * @returns Success or error
   */
  async unenroll(factorId: string): Promise<MFAResult<void>> {
    try {
      const userId = await this.getUserId();

      const result = await this.invoke<
        { user_id: string; factor_id: string },
        { success: true; message: string }
      >({
        functionName: 'unenroll-mfa',
        body: {
          user_id: userId,
          factor_id: factorId,
        },
        validate: (data): data is { success: true; message: string } =>
          typeof data === 'object' &&
          data !== null &&
          'success' in data &&
          'message' in data,
      });

      // Invalidate factor cache on success
      if (result.success) {
        this.cachedFactors = undefined;
      }

      // Convert { success: true, message } to void
      return result.success
        ? { success: true, data: undefined }
        : result;
    } catch (error) {
      return {
        success: false,
        error: {
          error_code: ErrorCode.UNAUTHORIZED,
          message: error instanceof Error ? error.message : 'Not authenticated',
        },
      };
    }
  }

  /**
   * Touch re-authentication timestamp
   *
   * Updates user's reauth_at timestamp after password verification.
   * Required before sensitive operations (enroll, unenroll).
   *
   * @returns Updated reauth timestamp
   */
  async touchReauth(): Promise<MFAResult<ReauthTouchResponse>> {
    try {
      const userId = await this.getUserId();

      return await this.invoke<
        { user_id: string },
        ReauthTouchResponse
      >({
        functionName: 'reauth-touch',
        body: { user_id: userId },
        validate: (data): data is ReauthTouchResponse =>
          typeof data === 'object' &&
          data !== null &&
          'reauth_at' in data,
      });
    } catch (error) {
      return {
        success: false,
        error: {
          error_code: ErrorCode.UNAUTHORIZED,
          message: error instanceof Error ? error.message : 'Not authenticated',
        },
      };
    }
  }

  // ===== PRIVATE HELPER METHODS =====

  /**
   * Report verification result to server
   *
   * Calls report-verify Edge Function to track attempts and emit telemetry.
   * This is non-blocking - failures are logged but don't affect the flow.
   *
   * @param userId - User ID
   * @param factorId - Factor ID
   * @param result - 'success' or 'fail'
   * @param errorType - Error classification (if failed)
   * @param nonce - Optional nonce from preflight
   * @returns Attempt count and server time (if available)
   */
  private async reportVerify(
    userId: string,
    factorId: string,
    result: 'success' | 'fail',
    errorType?: string,
    nonce?: string
  ): Promise<{ attempt_count?: number; server_time?: string }> {
    try {
      const reportResult = await this.invoke<
        {
          user_id: string;
          factor_id: string;
          op: string;
          result: string;
          error_type?: string;
          nonce?: string;
        },
        { recorded: true; attempt_count: number; server_time?: string }
      >({
        functionName: 'report-verify',
        body: {
          user_id: userId,
          factor_id: factorId,
          op: 'totp',
          result,
          ...(errorType && { error_type: errorType }),
          ...(nonce && { nonce }),
        },
        validate: (data): data is { recorded: true; attempt_count: number; server_time?: string } =>
          typeof data === 'object' && data !== null && 'recorded' in data,
      });

      if (reportResult.success) {
        return reportResult.data;
      }
    } catch (error) {
      console.warn('⚠️ Failed to report verification result (non-blocking):', error);
    }

    return {};
  }

  /**
   * Classify Supabase verify error
   *
   * Maps Supabase MFA error messages to our error taxonomy.
   *
   * @param error - Supabase error object
   * @returns Error type string
   */
  private classifyVerifyError(error: unknown): string {
    const msg = (error as { message?: string })?.message || '';
    if (/expire/i.test(msg)) return 'expired';
    if (/not.*found/i.test(msg)) return 'challenge_missing';
    return 'wrong_code';
  }

  /**
   * Map verify error to user-friendly message
   *
   * Provides contextual error messages based on error type and attempt count.
   * Includes clock skew hint after 3+ failures.
   *
   * @param error - Supabase error object
   * @param reportResult - Result from report-verify (includes attempt_count, server_time)
   * @returns User-friendly error message
   */
  private mapVerifyErrorMessage(
    error: unknown,
    reportResult: { attempt_count?: number; server_time?: string }
  ): string {
    const msg = (error as { message?: string })?.message || '';

    if (/expire/i.test(msg)) {
      return 'Verification session expired. Please try again.';
    }

    if (/not.*found/i.test(msg)) {
      return 'Verification session not found. Please try again.';
    }

    // Clock skew hint if ≥3 attempts
    if (reportResult.attempt_count && reportResult.attempt_count >= 3) {
      return 'Invalid code. If using an authenticator app, ensure your device time is accurate.';
    }

    return 'Invalid authentication code. Please try again.';
  }

  /**
   * Update factor label
   *
   * Updates the custom user-defined label for an MFA factor in the mfa_factors table.
   *
   * @param factorId - Factor ID to update
   * @param label - New label (e.g., "iPhone", "Work Phone")
   * @returns Updated factor on success
   */
  async updateFactorLabel(
    factorId: string,
    label: string
  ): Promise<MFAResult<undefined>> {
    const startTime = performance.now();

    try {
      // Validate inputs
      if (!factorId || typeof factorId !== 'string') {
        return {
          success: false,
          error: {
            error_code: ErrorCode.INVALID_REQUEST,
            message: 'Invalid factor ID',
          },
        };
      }

      if (!label || typeof label !== 'string' || label.trim().length === 0) {
        return {
          success: false,
          error: {
            error_code: ErrorCode.INVALID_REQUEST,
            message: 'Label cannot be empty',
          },
        };
      }

      // Trim and limit label length
      const sanitizedLabel = label.trim().substring(0, 100);

      // Update label in mfa_factors table
      const userId = await this.getUserId();

      try {
        await mfaFactorsRepository.updateLabel(userId, factorId, sanitizedLabel);
      } catch (error) {
        const duration = performance.now() - startTime;
        const errorMessage = error instanceof Error ? error.message : String(error);

        this.emitTelemetry({
          event: 'mfa_update_label_error',
          error: errorMessage,
          duration,
        });

        return {
          success: false,
          error: {
            error_code: ErrorCode.SERVER_ERROR,
            message: errorMessage,
          },
        };
      }

      // Invalidate cache to force refresh on next listFactors()
      this.cachedFactors = undefined;

      const duration = performance.now() - startTime;
      this.emitTelemetry({
        event: 'mfa_update_label_success',
        duration,
      });

      return { success: true, data: undefined };
    } catch (error) {
      const duration = performance.now() - startTime;

      this.emitTelemetry({
        event: 'mfa_update_label_error',
        error: error instanceof Error ? error.message : 'Unknown error',
        duration,
      });

      return {
        success: false,
        error: {
          error_code: ErrorCode.SERVER_ERROR,
          message:
            error instanceof Error
              ? error.message
              : 'Failed to update factor label',
        },
      };
    }
  }
}

// ===== SINGLETON EXPORT =====

/**
 * MFA Service singleton
 *
 * Import this to access all MFA operations.
 *
 * @example
 * import { mfaService } from '@/services/MFAService';
 *
 * const result = await mfaService.enrollStart();
 * if (!result.success) {
 *   console.error(result.error.message);
 *   return;
 * }
 *
 * const { qr_code, secret } = result.data;
 */
export const mfaService = new MFAService();
